import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { ClientButton } from '@/components/UI/Button';
import { useInterval } from '@/renderer/utils/hooks/useInterval';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { settingsButtonClass } from './SettingsSection';

const CODE_REGEX = /^[0-9]{6}$/;
const EMAIL_REGEX = /\S+@\S+\.\S+/;
// How long is a code valid until it expires?
const VERIFY_VALID_TIME = 24 * 60 * 60 * 1000;
// How long should the "get a new code" button be grayed out?
const RESEND_WAIT_TIME = 60 * 1000;

// Types
//
// Contacts are EMAIL-ONLY (#1799). The primary email plus the two alternate
// slots (contactSecondary / contactTertiary) are all email addresses, each
// verified via the Twilio Verify EMAIL channel. Phone numbers are no longer
// collected here — legacy numbers live in `userPhones` (win-back SMS only) and
// are shown read-only elsewhere on the settings page. This drops the old
// Phone/SMS tab and its `acceptSms` marketing-consent checkbox (SMS sunset,
// #1215).
type FormContactsData = {
  code?: string;
  email?: string;
  currentPassword?: string;
};

type Fields = 'email' | 'contactSecondary' | 'contactTertiary';

type UpdateContactsData<Field extends Fields> = {
  [key in Field]?: API.User[Field];
} & { [key in `${Field}Code`]?: string };

type UpdateContactsRequest<F extends Fields = 'contactSecondary'> = RequestSchema<
  API.WithData<API.User>,
  API.WithData<UpdateContactsData<F>, { resend?: Fields; cancel?: Fields }>
>;

// API requests
const updateContactsRequest: UpdateContactsRequest = {
  name: 'updateContactsRequest',
  input: '/api/v1/user/update/me?v2Verify=true',
  // input: '/api/v1/user/update/me',
  method: 'POST',
};

export default function UpdateContactsForm<const Field extends Fields>({
  onSuccess,
  setUser,
  user,
  name,
  onlyVerify,
  full,
  secondary,
  onCancelAdd,
}: {
  full?: boolean;
  onlyVerify?: boolean;
  onSuccess?: () => void | Promise<void>;
  setUser: (newUser: API.User | null) => void;
  user: API.User;
  name: Field;
  needsPassword?: boolean;
  /**
   * Lighter action-button hierarchy for the alternate-email slots: the primary
   * submit renders as a quiet secondary/link (not a heavy dark button) and the
   * destructive "Remove" renders as a muted text link that turns red on hover.
   */
  secondary?: boolean;
  /**
   * When present (alternate slots revealed via "+ Add another email"), renders a
   * quiet "Cancel" link that collapses the empty add form without saving.
   */
  onCancelAdd?: () => void;
}) {
  const userValue = user?.[name as Fields] || '';
  const metaValue = user?.meta?.[name];
  const metaUnverifiedDateValue = user?.meta?.[`${name}Unverified` as const];
  const metaUnverifiedDate = metaUnverifiedDateValue
    ? typeof metaUnverifiedDateValue === 'number'
      ? metaUnverifiedDateValue
      : Date.parse(metaUnverifiedDateValue)
    : -Infinity;
  const now = Date.now();
  const metaUnverifiedTimeDiff = now - metaUnverifiedDate;
  const isReadyToReceiveCode = !!metaValue && !!metaUnverifiedDate;
  const [isCodeExpired, setIsCodeExpired] = useState(
    isReadyToReceiveCode && metaUnverifiedTimeDiff > VERIFY_VALID_TIME,
  );
  const [disableResendButtonFor, setDisableResendButtonFor] = useState(
    (isReadyToReceiveCode &&
      Math.max(0, Math.ceil((RESEND_WAIT_TIME - metaUnverifiedTimeDiff) / 1000))) ||
      0,
  );
  const fieldValue = isReadyToReceiveCode ? metaValue : userValue;

  // Request hooks

  const request = useRequest(updateContactsRequest as UpdateContactsRequest<Field>, {
    manual: true,
  });

  // Timers

  const [countdownDelay, setCountdownDelay] = useState(1000);

  const countdownCallback = useCallback(() => {
    const innerNow = Date.now();
    const innerMetaUnverifiedTimeDiff = innerNow - metaUnverifiedDate;

    setDisableResendButtonFor(
      (isReadyToReceiveCode &&
        Math.max(0, Math.ceil((RESEND_WAIT_TIME - innerMetaUnverifiedTimeDiff) / 1000))) ||
        0,
    );
    setIsCodeExpired(isReadyToReceiveCode && innerMetaUnverifiedTimeDiff > VERIFY_VALID_TIME);

    if (isReadyToReceiveCode && innerMetaUnverifiedTimeDiff > RESEND_WAIT_TIME) {
      // Stop the interval
      setCountdownDelay(0);
    }
  }, [isReadyToReceiveCode, metaUnverifiedDate]);

  useInterval(countdownCallback, countdownDelay);

  // Work

  const [isSubmitted, setIsSubmitted] = useState<boolean | 'resend'>(false);

  const form = useForm<WithFormErrors<FormContactsData>>();
  const emailWatch = form.watch('email');
  const codeWatch = form.watch('code');
  const watchedValue = emailWatch === undefined ? fieldValue : emailWatch;

  // If they clicked a link from email to verify, pre-fill and submit the form
  // @todo Change the email template so:
  // 1. it properly outputs email address so we can validate it is the right one,
  // 2. uses fieldCode format instead of emailCode for all of them
  const searchParams = usePageContext().urlParsed?.search as
    | ({ [key in `${Fields}Code`]?: string } & { [key in `${Fields}Email`]?: string } & {
        email?: string;
      })
    | undefined;

  const autoSubmitted = useRef(false);

  useEffect(() => {
    if (
      searchParams?.emailCode &&
      ((!searchParams?.email && form.getValues().email) ||
        form.getValues().email === searchParams?.email)
    ) {
      form.setValue('code', searchParams.emailCode);

      if (autoSubmitted.current === false) {
        const pageCodeFields = document.getElementsByName('code');

        if (pageCodeFields.length === 1) {
          autoSubmitted.current = true;
          handleFormSubmit();
        }
      }
    }
  }, [searchParams?.emailCode, searchParams?.email]);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isSubmitted]);

  // Handlers
  const handleFormSubmit = useFormSubmit({
    form: form,
    callback: async (body, event) => {
      const fieldVal: API.User[Field] = body.email || '';

      try {
        // Special case: "get a new code" resend or cancel buttons were used
        const buttonName = ((event?.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement)
          ?.name;
        const isCancelButton = buttonName === 'cancel';
        const isResendButton = buttonName === 'resend';

        if (isResendButton) {
          setCountdownDelay(1000);
        }

        setIsSubmitted(isResendButton ? 'resend' : true);

        const response = await request.run(
          {
            data: {
              [name]: fieldVal,
              [`${name}Code`]: body.code,
            } as UpdateContactsData<Field>,
            meta: {
              resend: isResendButton ? name : undefined,
              cancel: isCancelButton ? name : undefined,
            },
          },
          { throwOnRun: true },
        );

        form.resetField('code');

        if (response) {
          setUser(response);
        }

        if (fieldVal && body.code) {
          await onSuccess?.();
        }
      } catch (error) {
        form.resetField('code');
        setIsSubmitted(false);

        throw error;
      }

      form.setValue('email', '');
    },
  });

  // The single submit button morphs between these actions depending on state.
  // A slot that already has a saved address always reads "Update" (disabled
  // until the field is edited), never "Add" — you can't add an email you
  // already have. Only empty slots show "Add".
  const verb = userValue
    ? watchedValue
      ? 'Update'
      : 'Remove'
    : isReadyToReceiveCode && metaValue === watchedValue && !isCodeExpired
      ? 'Confirm'
      : fieldValue && !watchedValue
        ? 'Cancel'
        : 'Add';
  // Alternate slots can be removed (the primary email can't). Show the quiet
  // Remove link whenever an alternate has a saved value and the user hasn't
  // typed a different one in its place (i.e. it's unchanged or cleared).
  const canRemove = name !== 'email';
  const showRemove = canRemove && !!userValue && (watchedValue === userValue || !watchedValue);
  const submitLabel = verb === 'Add' || verb === 'Update' ? `${verb} email` : verb;

  // Directly drop this alternate address (empty value, no code needed) — mirrors
  // the payload the morphing "Remove" submit used, but without making the user
  // clear the field first.
  const removeContact = async () => {
    try {
      setIsSubmitted(true);
      const response = await request.run(
        { data: { [name]: '' } as UpdateContactsData<Field> },
        { throwOnRun: true },
      );
      if (response) {
        setUser(response);
      }
      form.reset({ email: '' });
    } catch {
      setIsSubmitted(false);
    }
  };

  const emailForm = (
    <FormControl
      form={form}
      name="email"
      label="Email address"
      hideLabel
      className="max-w-sm"
      defaultValue={EMAIL_REGEX.test(fieldValue) ? fieldValue : ''}
      register={{
        pattern: {
          value: EMAIL_REGEX,
          message: 'Email is not a valid email!',
        },
      }}
    />
  );

  return (
    <form onSubmit={handleFormSubmit} method="POST" className="space-y-4">
      {onlyVerify && !!fieldValue ? (
        <h5 className="flex items-center space-x-2 border-zinc-900/5 border-b pb-2 text-xs text-zinc-700">
          <EnvelopeIcon height={20} className="size-5 text-inherit" aria-hidden="true" />
          <span>
            {userValue === fieldValue
              ? `Successfully verified ${fieldValue}`
              : `Verify ${fieldValue}`}
          </span>
        </h5>
      ) : (
        emailForm
      )}
      {!!watchedValue && isReadyToReceiveCode && metaValue === watchedValue && (
        <>
          {isCodeExpired ? (
            <aside className="bg-amber-50 p-4 dark:bg-amber-950/50">
              <div className="flex flex-row items-center space-x-2 text-amber-700 text-sm dark:text-amber-300">
                <span>
                  Your code expired for changing this to "{metaValue}". Please resubmit to get a new
                  code.
                </span>
              </div>
            </aside>
          ) : (
            <>
              <aside className="bg-green-50 p-4 dark:bg-green-950/50">
                <div className="flex flex-row items-end space-x-2 text-green-700 text-sm dark:text-green-300">
                  <span className="shrink grow-0">
                    <FormControl
                      form={form}
                      name="code"
                      label="Code"
                      inputMode="numeric"
                      minLength={6}
                      maxLength={6}
                      className="box-content w-[6ch] text-center font-mono"
                      register={{
                        pattern: {
                          value: CODE_REGEX,
                          message: 'Code must be 6 digits',
                        },
                      }}
                      autoComplete="off"
                      aria-autocomplete="none"
                      autoFocus={!!searchParams?.emailCode}
                    />
                  </span>
                  <span>
                    A 6-digit verification code has been sent via email. Enter it here within 24
                    hours to confirm.{' '}
                    <ClientButton
                      type="submit"
                      disabled={request.loading || disableResendButtonFor > 0}
                      loading={request.loading}
                      name="resend"
                      variant="link"
                      size="baseline"
                    >
                      Get a new code
                      {disableResendButtonFor > 0 && ` in ${disableResendButtonFor} seconds...`}
                    </ClientButton>
                  </span>
                </div>
              </aside>
            </>
          )}
          {!!userValue && (
            <aside className="bg-blue-50 p-4">
              <div className="flex flex-row items-center space-x-2 text-blue-700 text-sm">
                <span>
                  <strong className="bg-red-50">{userValue}</strong> will continue to be used until
                  you successfully verify the change.
                </span>
              </div>
            </aside>
          )}
        </>
      )}
      <aside className="bg-red-50 not-empty:p-4">
        <FormErrors form={form} request={request} ignoreMismatch={true} />
      </aside>
      {!(onlyVerify && !!fieldValue && userValue === fieldValue) && (
        <div className="flex flex-wrap items-center gap-4">
          {showRemove ? (
            <ClientButton
              type="button"
              variant="link"
              size="baseline"
              onClick={removeContact}
              disabled={request.loading}
              loading={request.loading}
              className="inline-flex items-center gap-1 p-0 font-medium text-zinc-500 no-underline hover:text-red-600 hover:no-underline dark:text-zinc-400 dark:hover:text-red-400"
            >
              <XMarkIcon className="size-4" aria-hidden="true" />
              Remove
            </ClientButton>
          ) : (
            <FormSubmitButton
              disabled={
                request.loading ||
                (watchedValue === fieldValue && !isCodeExpired && !codeWatch) ||
                (!watchedValue && name === 'email')
              }
              loading={request.loading}
              className={clsx(!secondary && !full && 'w-48', secondary && settingsButtonClass)}
              inline={!full}
            >
              {submitLabel}
            </FormSubmitButton>
          )}

          {onCancelAdd && !isReadyToReceiveCode && (
            <ClientButton
              type="button"
              variant="link"
              size="baseline"
              onClick={onCancelAdd}
              disabled={request.loading}
              className="p-0 font-medium text-zinc-500 no-underline hover:text-zinc-700 hover:underline dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              Cancel
            </ClientButton>
          )}

          {!!userValue && isReadyToReceiveCode && (
            <ClientButton
              type="submit"
              disabled={request.loading}
              loading={request.loading}
              name="cancel"
              variant="link"
              size="baseline"
            >
              Cancel
            </ClientButton>
          )}

          <span
            className={clsx(
              'text-zinc-500 leading-4 transition-opacity duration-500',
              isSubmitted ? 'opacity-100' : 'opacity-0',
            )}
          >
            {isSubmitted === 'resend' ? 'New code sent' : 'Contact updated successfully!'}
          </span>
        </div>
      )}
    </form>
  );
}
